import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Keeps exactly one free gift line in the cart while the trigger product is
 * there, and routes Buy it now on the trigger product through the cart so the
 * gift reaches checkout. Config comes from snippets/bonus-auto-inject.liquid.
 */

const configEl = document.getElementById('bonus-auto-inject-config');
const config = configEl ? JSON.parse(configEl.textContent || 'null') : null;
const PROPERTY = '_functionhub_bonus';

let syncing = false;

/** @param {any} item */
const isGift = (item) => Boolean(item.properties?.[PROPERTY]);
const giftLine = () => ({ id: config.giftVariantId, quantity: 1, properties: { [PROPERTY]: config.groupId } });
const getCart = () => fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
    .join(',');
}

/**
 * @param {string} url
 * @param {object} body
 */
async function post(url, body) {
  const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
  if (!response.ok) throw new Error(`[bonus-auto-inject] ${url} failed: ${response.status}`);
  return response.json();
}

/** @param {any[]} items */
async function sync(items) {
  if (syncing) return;

  const hasTrigger = items.some((item) => item.product_id === config.triggerProductId && !isGift(item));
  const gifts = items.filter(isGift);
  const needsAdd = hasTrigger && gifts.length === 0;
  const needsRemove = !hasTrigger && gifts.length > 0 && config.removeWithTrigger;
  if (!needsAdd && !needsRemove) return;

  syncing = true;
  const deferred = CartLinesUpdateEvent.createPromise();
  const sections = cartSectionIds() || undefined;

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: needsAdd ? 'add' : 'remove',
      context: 'cart',
      lines: needsAdd
        ? [{ merchandiseId: String(config.giftVariantId), quantity: 1 }]
        : gifts.map((item) => ({ id: item.key, quantity: 0 })),
      promise: deferred.promise,
    })
  );

  try {
    const response = needsAdd
      ? await post(Theme.routes.cart_add_url, { items: [giftLine()], sections })
      : await post(`${Theme.routes.cart_update_url}.js`, {
          updates: Object.fromEntries(gifts.map((item) => [item.key, 0])),
          sections,
        });
    // update.js returns the whole cart; add.js only the added lines.
    const cart = needsAdd ? await getCart() : response;

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'bonus-auto-inject', sections: response.sections, didError: false },
    });
  } catch (error) {
    deferred.reject(error);
    console.warn(error);
  } finally {
    syncing = false;
  }
}

/**
 * Buy it now normally checks out the single variant and skips the cart, so the
 * gift never gets added. Put the variant and gift in the cart, then check out.
 * @param {HTMLFormElement} form
 */
async function checkoutWithGift(form) {
  const variantId = Number(form.querySelector('[name="id"]')?.value);
  const quantity = Math.max(1, parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1);

  try {
    const { items } = await getCart();
    const line = items.find((item) => item.variant_id === variantId && !isGift(item));
    if (line && line.quantity < quantity) {
      await post(Theme.routes.cart_change_url, { id: line.key, quantity });
    }

    const toAdd = [];
    if (!line) toAdd.push({ id: variantId, quantity });
    if (!items.some(isGift)) toAdd.push(giftLine());
    if (toAdd.length) await post(Theme.routes.cart_add_url, { items: toAdd });
  } catch (error) {
    console.warn('[bonus-auto-inject] preparing Buy it now failed', error);
  }

  window.location.href = `${window.Shopify?.routes?.root ?? '/'}checkout`;
}

if (config) {
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    /** @type {CartLinesUpdateEvent} */ (event).promise
      ?.then(({ detail } = {}) => {
        if (detail?.source === 'bonus-auto-inject' || detail?.didError) return;
        return Array.isArray(detail?.items) ? sync(detail.items) : getCart().then((cart) => sync(cart.items));
      })
      .catch(() => {});
  });

  if (config.syncOnLoad) getCart().then((cart) => sync(cart.items)).catch(() => {});

  if (config.expressCheckout) {
    let checkingOut = false;

    // Capture phase, so this runs before Shopify's own button handler.
    document.addEventListener(
      'click',
      (event) => {
        const target = /** @type {Element} */ (event.target);
        const button = target.closest?.('shopify-accelerated-checkout, .shopify-payment-button');
        const form = /** @type {HTMLFormElement | null} */ (button?.closest('form[action*="/cart/add"]'));
        if (!form || Number(form.querySelector('[name="product-id"]')?.value) !== config.triggerProductId) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        if (checkingOut) return;
        checkingOut = true;
        checkoutWithGift(form);
      },
      true
    );
  }
}
